import { info } from '@ms/yammer-console-logging';
import type { PageLoadEventData, RouteCommittedEventData } from '@ms/yammer-page-route-telemetry';
import { subscribeToPageLoadEvent, unsubscribeFromPageLoadEvent } from '@ms/yammer-page-route-telemetry';
import type { PageLoadScenarioHandle, PageLoadScenarioRegistry } from '@ms/yammer-scenario-telemetry';
import { startPageLoadScenarioForPage } from '@ms/yammer-scenario-telemetry';

interface InFlightPageLoad {
  readonly pageLoadId: number;
  readonly handle: PageLoadScenarioHandle;
}

/**
 * Rebuilds an `Error` from a failed load's telemetry so the scenario keeps both of the hero's error
 * fields: `errorMessage` becomes the message and `errorCode` (the original error's name) becomes the
 * `Error.name`, so a failure identified only by a code is not reported as a nameless, messageless
 * `Error`. Returns `undefined` when the outcome carried neither (e.g. an expected recoverable state
 * with no attached error).
 */
const toScenarioError = ({ errorCode, errorMessage }: PageLoadEventData): Error | undefined => {
  if (!errorCode && !errorMessage) {
    return undefined;
  }

  const error = new Error(errorMessage);

  if (errorCode) {
    error.name = errorCode;
  }

  return error;
};

/**
 * Bridges page-route-telemetry's generic page-load lifecycle events onto scenario-telemetry's
 * page-load scenarios, for the pages in the app-provided `registry`. This is the only app-level
 * glue: it holds no scenario knowledge itself (which pages, names, sources all live in the
 * registry) and page-route-telemetry holds no scenario knowledge either — it just re-emits the
 * paint outcome it already computes.
 *
 * `RouteCommitted` (fired before paint, on initial load and every navigation) starts the
 * registered scenario for that page and abandons any previous in-flight one; the outcome-bearing
 * `Loaded`/`Failed`/`FailedRecoverable`/`FailedExpected` events (fired after style + layout, i.e. at
 * paint) settle it as `ok`/`failure`/`ignore` — a `FailedRecoverable` (an error with a recovery
 * path) and a `FailedExpected` (an expected, non-recoverable terminal state such as a deleted or
 * private resource) are both expected errors that must not count against reliability, so each
 * settles as `ignore` rather than `failure`. `Interrupted` (the user navigated away before paint)
 * abandons it promptly; a scenario that never resolves at all is auto-reported as `timeout` by the
 * façade, so `TimedOut` is intentionally left unhandled here.
 *
 * Outcome events are correlated to their start by `pageLoadId`, not by page name: two consecutive
 * navigations can share a `page` (e.g. thread → thread, or switching communities), so matching on
 * the name alone would let a late outcome from the previous navigation settle or cancel the current
 * scenario. `pageLoadId` is globally unique per navigation, so an exact-id match settles only the
 * navigation it belongs to and drops stale events.
 *
 * Each start/settle is mirrored to the console via `info` for dogfood observability; this does not
 * change the reported telemetry.
 *
 * Gated on `ScenarioBasedQosTelemetry` via the injected `isEnabled` (read at start time), so it
 * is inert when the experiment is off. Returns a teardown that removes all subscriptions.
 */
export const startPageLoadScenarioBridge = (
  registry: PageLoadScenarioRegistry,
  isEnabled: () => boolean
): (() => void) => {
  let inFlight: InFlightPageLoad | undefined;

  const settle = (data: PageLoadEventData, outcome: 'shown' | 'failed' | 'recoverable'): void => {
    if (!inFlight || inFlight.pageLoadId !== data.pageLoadId) {
      return;
    }

    const { handle } = inFlight;
    inFlight = undefined;

    if (outcome === 'shown') {
      handle.reportPageShown();

      return;
    }

    const error = toScenarioError(data);

    if (outcome === 'recoverable') {
      handle.reportRecoverablePageLoadFailure(error);
    } else {
      handle.reportPageLoadFailure(error);
    }
  };

  const startScenarioForCommittedRoute = ({ page, pageLoadId }: RouteCommittedEventData): void => {
    inFlight?.handle.abandon();
    inFlight = undefined;

    if (!isEnabled()) {
      return;
    }

    const handle = startPageLoadScenarioForPage(registry, page, {
      reportScenarioSettled: (status) => info('[page_load] scenario settled', { page, status }),
    });

    if (handle) {
      info('[page_load] scenario started', { page });
      inFlight = { pageLoadId, handle };
    }
  };

  const settleScenarioAsShown = (data: PageLoadEventData): void => settle(data, 'shown');
  const settleScenarioAsFailed = (data: PageLoadEventData): void => settle(data, 'failed');
  const settleScenarioAsRecoverableFailure = (data: PageLoadEventData): void => settle(data, 'recoverable');

  const abandonScenarioOnInterrupt = (data: PageLoadEventData): void => {
    if (!inFlight || inFlight.pageLoadId !== data.pageLoadId) {
      return;
    }

    const { handle } = inFlight;
    inFlight = undefined;
    handle.abandon();
  };

  subscribeToPageLoadEvent('RouteCommitted', startScenarioForCommittedRoute);
  subscribeToPageLoadEvent('Loaded', settleScenarioAsShown);
  subscribeToPageLoadEvent('Failed', settleScenarioAsFailed);
  subscribeToPageLoadEvent('FailedRecoverable', settleScenarioAsRecoverableFailure);
  subscribeToPageLoadEvent('FailedExpected', settleScenarioAsRecoverableFailure);
  subscribeToPageLoadEvent('Interrupted', abandonScenarioOnInterrupt);

  return () => {
    unsubscribeFromPageLoadEvent('RouteCommitted', startScenarioForCommittedRoute);
    unsubscribeFromPageLoadEvent('Loaded', settleScenarioAsShown);
    unsubscribeFromPageLoadEvent('Failed', settleScenarioAsFailed);
    unsubscribeFromPageLoadEvent('FailedRecoverable', settleScenarioAsRecoverableFailure);
    unsubscribeFromPageLoadEvent('FailedExpected', settleScenarioAsRecoverableFailure);
    unsubscribeFromPageLoadEvent('Interrupted', abandonScenarioOnInterrupt);
    inFlight?.handle.abandon();
    inFlight = undefined;
  };
};
